import {
  calculatePurchasedGroupAmounts,
  normalizeGroupTicketConfiguration,
  validateGroupSelection,
} from './projections.js';

const asInteger = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : fallback;
};

const ticketType = (ticket) => {
  if (ticket?.type === 'SINGLE' || ticket?.type === 'GROUP') return ticket.type;
  if (ticket?.ticketType === 'INDIVIDUAL') return 'SINGLE';
  if (ticket?.ticketType === 'GROUP') return 'GROUP';
  return asInteger(ticket?.seats, 1) > 1 ? 'GROUP' : 'SINGLE';
};

/** Normalizes public v2 and legacy ticket configuration for buyer UIs. */
export const normalizeBuyerTicketConfiguration = (ticket, ticketContractVersion) => {
  const type = ticketType(ticket);
  if (type === 'SINGLE') {
    return {
      ...ticket,
      type,
      quantity: asInteger(ticket.quantity),
      sold: asInteger(ticket.sold),
    };
  }

  const isV2 = ticketContractVersion === 2 || ticket.groupSizeMode != null;
  const candidate = {
    ...ticket,
    type,
    quantity: asInteger(ticket.quantity),
    sold: asInteger(ticket.sold),
    seatCapacity: asInteger(ticket.seatCapacity),
    seatsSold: asInteger(ticket.seatsSold),
  };
  if (ticket.groupSizeMode === 'FIXED') {
    candidate.seats = asInteger(ticket.groupSize ?? ticket.seats);
  } else if (ticket.groupSizeMode == null) {
    candidate.seats = asInteger(ticket.seats);
  } else {
    delete candidate.seats;
  }
  if (!isV2) return normalizeGroupTicketConfiguration(candidate);
  return normalizeGroupTicketConfiguration(candidate);
};

export const getBuyerGroupSeatBounds = (ticket, ticketContractVersion) => {
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  if (normalized.type !== 'GROUP') return null;
  const minimum = normalized.groupSizeMode === 'FIXED' ? normalized.seats : normalized.minimumSeats;
  const configuredMaximum =
    normalized.groupSizeMode === 'FIXED' ? normalized.seats : normalized.maximumSeats;
  const remainingSeats = Math.max(normalized.seatCapacity - normalized.seatsSold, 0);
  return {
    minimum,
    maximum:
      configuredMaximum == null ? remainingSeats : Math.min(configuredMaximum, remainingSeats),
    remainingGroups: Math.max(normalized.quantity - normalized.sold, 0),
    remainingSeats,
  };
};

export const calculateBuyerGroupAmount = (ticket, seatCount, ticketContractVersion) => {
  const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
  const validated = validateGroupSelection({ ticket: normalized, seatCount });
  const amounts = calculatePurchasedGroupAmounts({
    pricingMode: validated.ticket.pricingMode,
    unitPriceMinor: Math.round(Number(ticket.price) * 100),
    seatCount: validated.seatCount,
  });
  return amounts.totalAmountMinor / 100;
};

export const createBuyerPurchasedGroup = ({ ticket, ticketContractVersion, purchasedGroupID }) => {
  const bounds = getBuyerGroupSeatBounds(ticket, ticketContractVersion);
  if (!bounds) throw new TypeError('Only Group Tickets can create purchased groups');
  validateGroupSelection({
    ticket: normalizeBuyerTicketConfiguration(ticket, ticketContractVersion),
    seatCount: bounds.minimum,
  });
  return {
    purchasedGroupID,
    ticketID: ticket.id,
    seatCount: bounds.minimum,
  };
};

export const resizeBuyerPurchasedGroup = ({
  ticket,
  ticketContractVersion,
  purchasedGroup,
  seatCount,
}) => {
  validateGroupSelection({
    ticket: normalizeBuyerTicketConfiguration(ticket, ticketContractVersion),
    seatCount,
  });
  return { ...purchasedGroup, seatCount };
};

/**
 * Creates the exact wire projection from buyer-local ticket state. Group
 * identity comes only from selectedGroups; it is never reconstructed by
 * attendee chunking.
 */
export const projectBuyerTicketPurchase = ({ ticketContractVersion, tickets }) => {
  const selectedTickets = Array.isArray(tickets) ? tickets : [];
  const attendees = selectedTickets.flatMap((ticket) => ticket.attendees ?? []);

  if (ticketContractVersion !== 2) {
    const ticketMapping = selectedTickets.map((ticket) => ({
      id: ticket.id,
      name: ticket.name,
      numSold: asInteger(ticket.soldNow),
      soldTotalPrice: Number(ticket.soldNowPrice) || 0,
    }));
    return {
      ticketContractVersion: undefined,
      purchasedGroups: undefined,
      tickets: attendees,
      ticketMapping,
      displayLineItems: ticketMapping.map((mapping) => ({
        key: `legacy:${mapping.id}`,
        ticketID: mapping.id,
        ticketName: mapping.name,
        quantity: mapping.numSold,
        seatCount: null,
        amount: mapping.numSold === 0 ? 0 : mapping.soldTotalPrice / mapping.numSold,
        purchasedGroupID: null,
        type: 'LEGACY',
      })),
      amount: ticketMapping.reduce((sum, mapping) => sum + mapping.soldTotalPrice, 0),
    };
  }

  const purchasedGroups = selectedTickets.flatMap((ticket) => ticket.selectedGroups ?? []);
  const mapping = selectedTickets.map((ticket) => {
    const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
    if (normalized.type === 'GROUP') {
      const groups = ticket.selectedGroups ?? [];
      return {
        id: ticket.id,
        name: ticket.name,
        numSold: groups.length,
        soldTotalPrice: groups.reduce(
          (sum, group) =>
            sum + calculateBuyerGroupAmount(ticket, group.seatCount, ticketContractVersion),
          0,
        ),
      };
    }
    return {
      id: ticket.id,
      name: ticket.name,
      numSold: (ticket.attendees ?? []).length,
      soldTotalPrice: (ticket.attendees ?? []).reduce(
        (sum, attendee) => sum + (Number(attendee.price) || 0),
        0,
      ),
    };
  });
  const displayLineItems = selectedTickets.flatMap((ticket) => {
    const normalized = normalizeBuyerTicketConfiguration(ticket, ticketContractVersion);
    if (normalized.type === 'GROUP') {
      return (ticket.selectedGroups ?? []).map((group) => ({
        key: group.purchasedGroupID,
        ticketID: ticket.id,
        ticketName: ticket.name,
        quantity: 1,
        seatCount: group.seatCount,
        amount: calculateBuyerGroupAmount(ticket, group.seatCount, ticketContractVersion),
        purchasedGroupID: group.purchasedGroupID,
        type: 'GROUP',
      }));
    }
    const quantity = (ticket.attendees ?? []).length;
    return quantity === 0
      ? []
      : [
          {
            key: `individual:${ticket.id}`,
            ticketID: ticket.id,
            ticketName: ticket.name,
            quantity,
            seatCount: 1,
            amount: Number(ticket.price) || 0,
            purchasedGroupID: null,
            type: 'SINGLE',
          },
        ];
  });

  return {
    ticketContractVersion: 2,
    purchasedGroups: purchasedGroups.map(({ purchasedGroupID, ticketID, seatCount }) => ({
      purchasedGroupID,
      ticketID,
      seatCount,
    })),
    tickets: attendees,
    ticketMapping: mapping,
    displayLineItems,
    amount: mapping.reduce((sum, item) => sum + item.soldTotalPrice, 0),
  };
};
